import React from 'react';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Card, CardContent, Typography, Box, Stack, Divider } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const RiskDistributionCard = ({ summaryData, onCardClick, selectedClassification }) => {
  if (!summaryData) {
    return (
      <Grid2 container>
        <Grid2 xs={12}>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}
          >
            No risk distribution data available
          </Typography>
        </Grid2>
      </Grid2>
    );
  }

  const cards = [
    {
      classification: 'CRITICAL_EXCESS',
      label: 'Critical Excess',
      Icon: ErrorIcon,
      muiColor: 'error.main',
      subtitle: 'Immediate Action',
      count: summaryData.critical_count || 0,
      percentage: summaryData.critical_percentage || 0,
    },
    {
      classification: 'HIGH_EXCESS',
      label: 'High Excess',
      Icon: WarningAmberIcon,
      muiColor: 'warning.main',
      subtitle: 'This Week',
      count: summaryData.high_count || 0,
      percentage: summaryData.high_percentage || 0,
    },
    {
      classification: 'MODERATE_EXCESS',
      label: 'Moderate Excess',
      Icon: InfoIcon,
      muiColor: 'info.main',
      subtitle: 'This Month',
      count: summaryData.moderate_count || 0,
      percentage: summaryData.moderate_percentage || 0,
    },
    {
      classification: 'ACCEPTABLE',
      label: 'Acceptable',
      Icon: CheckCircleIcon,
      muiColor: 'success.main',
      subtitle: 'Monitored',
      count: summaryData.acceptable_count || 0,
      percentage: summaryData.acceptable_percentage || 0,
    },
  ];

  return (
    <Grid2 container spacing={2}>
      {cards.map((card) => {
        const { Icon } = card;
        const isSelected = selectedClassification === card.classification;

        return (
          <Grid2 key={card.classification} xs={12} sm={6} lg={3}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.2s',
                ...(isSelected && {
                  borderWidth: 2,
                  borderStyle: 'solid',
                  borderColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? `${theme.palette[card.muiColor.split('.')[0]]?.main}B3`
                      : `${theme.palette[card.muiColor.split('.')[0]]?.main}80`,
                }),
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: card.muiColor,
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => onCardClick(card.classification)}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        letterSpacing: '0.8px',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {card.label}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ color: card.muiColor, fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {card.count}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'background.subtle' }}>
                    <Icon sx={{ color: card.muiColor, fontSize: 22 }} />
                  </Box>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {card.subtitle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {card.percentage.toFixed(1)}%
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid2>
        );
      })}
    </Grid2>
  );
};

export default RiskDistributionCard;
